import axios from "axios";
import { markSync } from "@/lib/lastSync";
import { getAdminCode, clearAdminCode } from "@/components/AdminCodeGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Add bearer token to requests (works alongside cookies)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const adminCode = getAdminCode();
  if (adminCode) {
    config.headers = config.headers || {};
    config.headers["X-Admin-Code"] = adminCode;
  }
  return config;
});

// Auto-refresh on 401: se l'access token è scaduto ma esiste un refresh valido
// (cookie httpOnly), otteniamo un nuovo access e riprovare la richiesta originale.
// Se il refresh fallisce, deleghiamo al chiamante (l'AuthContext mostrerà login).
let _refreshInFlight = null;

async function _tryRefreshToken() {
  if (!_refreshInFlight) {
    _refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .then((r) => {
        const t = r?.data?.access_token;
        if (t) localStorage.setItem("access_token", t);
        return t;
      })
      .catch(() => null)
      .finally(() => {
        // Consenti tentativi futuri anche se questo fallisce
        setTimeout(() => { _refreshInFlight = null; }, 0);
      });
  }
  return _refreshInFlight;
}

api.interceptors.response.use(
  (r) => {
    // Ogni risposta buona = copia locale fresca (usata per il messaggio "dati al …").
    if (r?.config?.method === "get" && !String(r.config.url || "").includes("/auth/")) markSync();
    return r;
  },
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config;
    const url = (cfg?.url || "");
    if (status === 403 && error?.response?.data?.detail === "ADMIN_CODE_REQUIRED" && cfg?.headers?.["X-Admin-Code"]) {
      // Il token di sblocco inviato non è più valido: richiudi le pagine riservate.
      if (cfg.headers["X-Admin-Code"] === getAdminCode()) {
        clearAdminCode();
        window.dispatchEvent(new Event("admin-code-required"));
      }
    }
    // Non provare a refreshare sugli endpoint auth stessi per evitare loop
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && cfg && !cfg._retry && !isAuthEndpoint) {
      cfg._retry = true;
      const newToken = await _tryRefreshToken();
      if (newToken) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return api(cfg);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Errore inatteso. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Errori su download blob: il JSON {detail} arriva come Blob, lo leggiamo in modo asincrono.
export async function extractErrorAsync(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return formatApiError(JSON.parse(await data.text())?.detail); } catch { /* corpo non JSON */ }
  }
  return extractError(e);
}

export function extractError(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Errore";
}
